export const commercialTermsLabel = (quote) => quote?.prepared?.paymentTerms || (quote?.terms === 'net60' ? 'Net 60' : 'Net 30');

export function commercialExpiryLabel(value) {
  return new Date(value).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
}

const serviceLabels = {
  weekly: 'Weekly service', repair: 'Repairs', filter_clean: 'Filter cleaning', salt_cell_clean: 'Salt cell cleaning',
  green_to_clean: 'Green-to-clean', drain_and_fill: 'Drain and fill', spa_only: 'Spa-only service',
  one_time: 'One-time service', chemical_only: 'Chemical-only service', commercial: 'Commercial service', other: 'Other',
};
// A prepared quote prices every charge it carries. The standing annual algae
// treatment is not one of them, so it is neither shown on an unsigned prepared
// proposal nor captured when one is signed. Apply this only to the current
// template; a signed copy renders the content it froze.
const STANDING_ALGAE_SERVICE = 'Annual algae prevention treatment';
export function commercialProposalContent(content, prepared) {
  if (!prepared || !content) return content;
  return { ...content, additionalServices: (content.additionalServices || []).filter((s) => s.title !== STANDING_ALGAE_SERVICE) };
}

// Whether the $100 algae row belongs in the pricing block. Ordinary quotes
// always carried it; a prepared copy carries it only when its content still
// lists the service, as copies signed before the prepared terms dropped it do.
export function showsStandingAlgaeCharge(content, prepared) {
  return !prepared || (content?.additionalServices || []).some((s) => s.title === STANDING_ALGAE_SERVICE);
}

const hasText = value => typeof value === 'string' && !!value.trim();

// Shared by the proposal, transactional email and executed archive.
export function preparedCommercialDetails(s) {
  if (!s) return [];
  const t = s.commercialTerms;
  const rows = [
    ['Authorized signer', `${t.signer.name}, ${t.signer.email}${t.signer.phone ? `, ${t.signer.phone}` : ''}`],
    ['Billing contact', `${t.billing.name}, ${t.billing.email}${t.billing.phone ? `, ${t.billing.phone}` : ''}`],
    ...(t.onsite ? [['On-site contact', `${t.onsite.name}, ${t.onsite.email}${t.onsite.phone ? `, ${t.onsite.phone}` : ''}`]] : []),
    ...s.products.map((p) => [p.name, `${p.quantity} × $${p.unitPrice} USD, one time`]),
    ...(s.serviceDetails?.priceAdjustments || []).map((a) => ['Agreed price adjustment', `${s.products.find((p) => String(p.id) === String(a.productId))?.name}: $${a.referencePrice} to $${a.unitPrice} USD per unit. ${a.reason}`]),
    ...s.discounts.map((d, i) => [`One-time discount${s.discounts.length > 1 ? ` ${i + 1}` : ''}`, `$${d.amount} USD`]),
    ...(s.promo ? [['Promotion', s.promo]] : []),
    [t.seasonal ? 'Summer monthly total' : 'Monthly total', `$${s.totals.recurring} USD`],
    ...(t.seasonal ? [['Winter monthly total', `$${t.winterRate} USD, ${t.winterFrequency}`]] : []),
    ['One-time total after discounts', `$${s.totals.oneTime} USD`],
    ['Payment terms', s.paymentTerms],
    ['Proposal expires', commercialExpiryLabel(s.expiry)],
  ];
  const labels = { hasSpa: 'Spa', hasSalt: 'Salt system', serviceTier: 'Service level', services: 'Services', poolCondition: 'Pool condition', equipment: 'Equipment', poolSize: 'Pool size' };
  for (const [key, label] of Object.entries(labels)) {
    const value = s.serviceDetails?.[key];
    if (key === 'equipment') {
      for (const equipment of Array.isArray(value) ? value : []) {
        const description = [hasText(equipment?.type) ? equipment.type : '',
          hasText(equipment?.model) ? `Model: ${equipment.model}` : '',
          hasText(equipment?.notes) ? `Notes: ${equipment.notes}` : ''].filter(Boolean).join('; ');
        if (description) rows.push([label, description]);
      }
    } else if (key === 'services') {
      const description = (Array.isArray(value) ? value : []).filter(hasText).map(service => Object.hasOwn(serviceLabels, service) ? serviceLabels[service] : service).join(', ');
      if (description) rows.push([label, description]);
    } else if (value != null && (typeof value !== 'string' || hasText(value))) {
      rows.push([label, typeof value === 'boolean' ? value ? 'Yes' : 'No' : String(value)]);
    }
  }
  if (t.documentTerms) rows.push(['Additional agreed terms', t.documentTerms]);
  return rows;
}
